import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ArrowUp, ArrowDown, Trash2, Upload, Save, Loader2, ShieldCheck, LogOut } from 'lucide-react';
import { adminApi, logoutAdmin } from './adminApi';
import { toAssetUrl } from '@/data/runtimeApi';

const toUrl = toAssetUrl;

export default function AdminClients() {
  const [items, setItems] = useState([]);
  const [speed, setSpeed] = useState(50);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [newName, setNewName] = useState('');
  const fileRef = useRef();

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await adminApi.get('/clients');
      setItems(data.clients || []);
      setSpeed(data.speed || 50);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const move = async (idx, dir) => {
    const to = idx + dir;
    if (to < 0 || to >= items.length) return;
    const arr = [...items];
    [arr[idx], arr[to]] = [arr[to], arr[idx]];
    setItems(arr);
    await adminApi.post('/clients/reorder', arr.map((x) => x.id));
  };

  const setWidth = async (id, pct) => {
    setItems((cur) => cur.map((x) => x.id === id ? { ...x, width_pct: pct } : x));
  };
  const saveWidth = async (id, pct) => {
    await adminApi.patch(`/clients/${id}`, { width_pct: pct });
  };

  const del = async (id, name) => {
    if (!window.confirm(`¿Eliminar el logo de "${name}"?`)) return;
    try {
      const { data } = await adminApi.delete(`/clients/${id}`);
      // Trust server response — avoids stale-refetch bug on CDN/production
      setItems(data.clients || []);
      if (typeof data.speed === 'number') setSpeed(data.speed);
    } catch (e) {
      alert(e?.response?.data?.detail || 'Error al eliminar');
    }
  };

  const clearAll = async () => {
    if (!window.confirm(`¿Eliminar TODOS los ${items.length} logos del slider?\n\nEsta acción no se puede deshacer.`)) return;
    try {
      const { data } = await adminApi.delete('/clients');
      setItems(data.clients || []);
      if (typeof data.speed === 'number') setSpeed(data.speed);
    } catch (e) {
      alert(e?.response?.data?.detail || 'Error');
    }
  };

  const upload = async () => {
    const f = fileRef.current?.files?.[0];
    if (!f) { alert('Selecciona una imagen'); return; }
    if (!newName.trim()) { alert('Ingresa el nombre del cliente'); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', f);
      fd.append('name', newName);
      await adminApi.post('/clients/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setNewName('');
      if (fileRef.current) fileRef.current.value = '';
      load();
    } catch (e) {
      alert(e?.response?.data?.detail || 'Error al subir');
    } finally { setUploading(false); }
  };

  const saveSpeed = async () => {
    setSaving(true);
    try {
      await adminApi.patch('/clients/settings/carousel', { speed });
    } finally { setSaving(false); }
  };

  const onLogout = () => { logoutAdmin(); window.location.href = '/admin/login'; };

  return (
    <div className="min-h-screen bg-[#f4f5f7]" data-testid="admin-clients-page">
      <header className="bg-white border-b border-[#58677A]/10 sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/admin" className="text-[#58677A] hover:text-[#ED6B24] transition"><ArrowLeft size={18}/></Link>
            <div className="w-9 h-9 rounded-lg bg-[#ED6B24]/10 flex items-center justify-center"><ShieldCheck className="text-[#ED6B24]" size={18} /></div>
            <div>
              <p className="text-[15px] font-bold text-[#58677A] leading-tight" style={{fontFamily:'Gotham,sans-serif'}}>Slider de clientes</p>
              <p className="text-[11px] text-[#58677A]/60">Home</p>
            </div>
          </div>
          <button onClick={onLogout} className="inline-flex items-center gap-1.5 text-xs text-[#58677A] hover:text-red-600 px-3 py-1.5 rounded-md hover:bg-red-50" data-testid="admin-logout"><LogOut size={13}/> Salir</button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8 space-y-8">
        {/* Speed */}
        <section className="bg-white rounded-2xl border border-[#58677A]/10 p-5 sm:p-6">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-bold text-[#58677A]" style={{fontFamily:'Gotham,sans-serif'}}>Velocidad del carrusel</h2>
            <button onClick={saveSpeed} disabled={saving} className="inline-flex items-center gap-1.5 bg-[#ED6B24] hover:bg-[#ff8242] text-white text-xs font-semibold rounded-full px-4 py-1.5 disabled:opacity-60" data-testid="save-speed"><Save size={13}/>{saving ? 'Guardando…' : 'Guardar'}</button>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs text-[#58677A]/70 w-14">Lento</span>
            <input type="range" min={5} max={200} value={speed} onChange={(e) => setSpeed(parseInt(e.target.value))} className="flex-1 accent-[#ED6B24]" data-testid="speed-slider"/>
            <span className="text-xs text-[#58677A]/70 w-14 text-right">Rápido</span>
            <span className="ml-3 text-xs font-mono bg-[#ED6B24]/10 text-[#ED6B24] rounded px-2 py-1 w-16 text-center">{speed} px/s</span>
          </div>
        </section>

        {/* Upload */}
        <section className="bg-white rounded-2xl border border-[#58677A]/10 p-5 sm:p-6">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
            <h2 className="font-bold text-[#58677A]" style={{fontFamily:'Gotham,sans-serif'}}>Añadir logotipo</h2>
            {items.length > 0 && (
              <button onClick={clearAll} className="inline-flex items-center gap-1.5 text-red-600 border border-red-300 hover:bg-red-500 hover:text-white text-xs font-semibold rounded-md px-3 py-1.5" data-testid="clear-clients-btn">
                <Trash2 size={12}/> Eliminar todos ({items.length})
              </button>
            )}
          </div>
          <div className="grid sm:grid-cols-[1fr_1fr_auto] gap-3 items-end">
            <div>
              <label className="text-xs text-[#58677A]/70 block mb-1">Nombre del cliente</label>
              <input type="text" value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="ej. Marathon" className="w-full border border-[#58677A]/20 rounded-lg px-3 py-2 focus:outline-none focus:border-[#ED6B24]" data-testid="new-client-name"/>
            </div>
            <div>
              <label className="text-xs text-[#58677A]/70 block mb-1">Archivo (PNG con fondo transparente ideal)</label>
              <input ref={fileRef} type="file" accept="image/*" className="w-full text-sm text-[#58677A] file:mr-3 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:bg-[#ED6B24]/10 file:text-[#ED6B24] file:font-semibold" data-testid="new-client-file"/>
            </div>
            <button onClick={upload} disabled={uploading} className="inline-flex items-center gap-2 bg-[#ED6B24] hover:bg-[#ff8242] text-white text-sm font-semibold rounded-lg px-4 py-2 disabled:opacity-60" data-testid="upload-client">{uploading ? <Loader2 size={15} className="animate-spin"/> : <Upload size={15}/>} Subir</button>
          </div>
        </section>

        {/* List */}
        <section className="bg-white rounded-2xl border border-[#58677A]/10">
          <div className="px-5 sm:px-6 py-4 border-b border-[#58677A]/10">
            <h2 className="font-bold text-[#58677A]" style={{fontFamily:'Gotham,sans-serif'}}>Logotipos actuales ({items.length})</h2>
            <p className="text-xs text-[#58677A]/60 mt-0.5">Arrastra o usa las flechas para reordenar. Ajusta el ancho con el deslizador (altura proporcional).</p>
          </div>
          {loading && <div className="p-8 text-center text-[#58677A]/60"><Loader2 className="mx-auto animate-spin mb-2"/>Cargando…</div>}
          <ul className="divide-y divide-[#58677A]/10">
            {items.map((c, i) => (
              <li key={c.id} className="grid grid-cols-[auto_120px_1fr_240px_auto] items-center gap-4 px-5 sm:px-6 py-3" data-testid={`client-row-${c.id}`}>
                <div className="flex flex-col gap-1">
                  <button onClick={() => move(i, -1)} disabled={i===0} className="w-7 h-7 rounded flex items-center justify-center text-[#58677A] hover:bg-[#ED6B24]/10 disabled:opacity-30" data-testid="move-up"><ArrowUp size={14}/></button>
                  <button onClick={() => move(i, 1)} disabled={i===items.length-1} className="w-7 h-7 rounded flex items-center justify-center text-[#58677A] hover:bg-[#ED6B24]/10 disabled:opacity-30" data-testid="move-down"><ArrowDown size={14}/></button>
                </div>
                <div className="bg-[#f4f5f7] rounded-md h-14 flex items-center justify-center overflow-hidden">
                  <img src={toUrl(c.logo)} alt={c.name} className="max-h-10 object-contain" style={{ maxWidth: `${c.width_pct}%` }}/>
                </div>
                <div className="text-sm font-medium text-[#58677A] truncate">{c.name}</div>
                <div className="flex items-center gap-2">
                  <span className="text-[10px] text-[#58677A]/60">Ancho</span>
                  <input type="range" min={20} max={250} value={c.width_pct} onChange={(e) => setWidth(c.id, parseInt(e.target.value))} onMouseUp={(e) => saveWidth(c.id, parseInt(e.target.value))} onTouchEnd={(e) => saveWidth(c.id, parseInt(e.target.value))} className="flex-1 accent-[#ED6B24]" data-testid={`width-slider-${c.id}`}/>
                  <span className="text-[11px] font-mono w-12 text-right text-[#ED6B24]">{c.width_pct}%</span>
                </div>
                <button onClick={() => del(c.id, c.name)} className="text-red-500 hover:bg-red-50 w-8 h-8 rounded flex items-center justify-center" title="Eliminar" data-testid={`delete-${c.id}`}><Trash2 size={15}/></button>
              </li>
            ))}
          </ul>
        </section>
      </main>
    </div>
  );
}
